// src/components/FeaturedVideos.jsx
import { useEffect, useRef, useState } from 'react'
import { Timestamp } from 'firebase/firestore'
import { fetchAllFeaturedVideos } from '../lib/featuredVideos'

function isRowVisibleNow(row) {
  const now = new Date()

  const parseTs = (ts) => {
    if (!ts) return null
    if (ts instanceof Timestamp) return ts.toDate()
    return new Date(ts)
  }

  const start = parseTs(row.startsAt)
  const end = parseTs(row.endsAt)

  if (start && now < start) return false
  if (end && now > end) return false

  return !!row.isActive
}

/** 🎥 Une carte vidéo avec contrôles custom */
function FeaturedVideoCard({ v }) {
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(true)

  const price = Number(v.price || 0)

  const handleMouseEnter = () => {
    const video = videoRef.current
    if (!video) return
    try {
      video.muted = true
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {})
    } catch {}
  }

  const handleMouseLeave = () => {
    const video = videoRef.current
    if (!video) return
    try {
      video.pause()
      video.currentTime = 0
      setIsPlaying(false)
    } catch {}
  }

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (isPlaying) {
      video.pause()
      setIsPlaying(false)
    } else {
      video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {})
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return
    const nextMuted = !isMuted
    video.muted = nextMuted
    setIsMuted(nextMuted)
  }

  return (
    <article
      className="fv-card"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Bloc vidéo vertical style TikTok */}
      <div className="fv-video-wrapper">
        {v.videoUrl ? (
          <video
            ref={videoRef}
            src={v.videoUrl}
            className="fv-video"
            muted={isMuted}
            loop
            playsInline
            controls={false} // ❌ pas de gros contrôles natifs
          />
        ) : (
          <div className="fv-video-placeholder">
            Vidéo indisponible
          </div>
        )}

        {/* Handle créateur */}
        {v.creatorHandle && (
          <div className="fv-overlay-pill" style={{ top: '8px', left: '8px' }}>
            {v.creatorHandle}
          </div>
        )}

        {/* 🔘 Contrôles custom play / mute */}
        <div className="fv-controls">
          <button
            type="button"
            className="fv-control-btn"
            onClick={togglePlay}
          >
            <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`} />
          </button>
          <button
            type="button"
            className="fv-control-btn"
            onClick={toggleMute}
          >
            <i
              className={`bi ${
                isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Infos produit */}
      <div className="fv-body">
        {v.title && (
          <div className="fv-title line-clamp-2">
            {v.title}
          </div>
        )}

        <div className="fv-product">
          <div className="fv-product-thumb">
            {v.productImage ? (
              <img
                src={v.productImage}
                alt={v.productName || 'Produit'}
              />
            ) : (
              <div className="fv-product-placeholder" />
            )}
          </div>

          <div className="fv-product-info">
            <div className="fv-product-name line-clamp-2">
              {v.productName || 'Produit mis en avant'}
            </div>
            <div className="fv-price-row">
              <span className="fv-price-main">
                {price > 0
                  ? `${price.toLocaleString()} ${v.currency || 'XOF'}`
                  : 'Voir le prix'}
              </span>
            </div>

            {v.productHref && (
              <a
                href={v.productHref}
                className="btn btn-primary btn-sm fv-cta"
              >
                Voir le produit
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}

export default function FeaturedVideos() {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // 👉 ref sur la piste scrollable
  const trackRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const rows = await fetchAllFeaturedVideos()

        const filtered = rows
          .filter(isRowVisibleNow)
          .sort((a, b) => (a.order || 0) - (b.order || 0))

        setVideos(filtered)
      } catch (e) {
        console.error(e)
        setError(e?.message || 'Erreur de chargement des vidéos.')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // 👉 gestion des flèches gauche/droite
  const updateArrows = () => {
    const el = trackRef.current
    if (!el) return
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 10)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 10)
  }

  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    updateArrows()

    const onScroll = () => updateArrows()
    const onResize = () => updateArrows()

    el.addEventListener('scroll', onScroll)
    window.addEventListener('resize', onResize)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [videos])

  const scrollTrack = (direction) => {
    const el = trackRef.current
    if (!el) return
    const amount = el.clientWidth * 0.8 // ~80% de la largeur visible
    const next =
      direction === 'left'
        ? el.scrollLeft - amount
        : el.scrollLeft + amount

    el.scrollTo({
      left: next,
      behavior: 'smooth',
    })
  }

  if (loading) {
    return (
      <section className="featured-videos my-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">En vedette dans les vidéos</h5>
        </div>
        <div className="fv-track">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="fv-card skeleton" key={i}>
              <div className="fv-video-skeleton shimmer" />
              <div className="fv-body">
                <div className="fv-title-skeleton shimmer" />
                <div className="fv-meta-skeleton shimmer" />
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  if (error || videos.length === 0) {
    return null
  }

  return (
    <section className="featured-videos my-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">En vedette dans les vidéos</h5>
      </div>

      {/* wrapper pour pouvoir placer les flèches */}
      <div className="fv-track-wrapper position-relative">
        {canScrollLeft && (
          <button
            type="button"
            className="fv-arrow fv-arrow-left"
            onClick={() => scrollTrack('left')}
            aria-label="Voir les vidéos précédentes"
          >
            ‹
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            className="fv-arrow fv-arrow-right"
            onClick={() => scrollTrack('right')}
            aria-label="Voir les vidéos suivantes"
          >
            ›
          </button>
        )}

        <div className="fv-track" ref={trackRef}>
          {videos.map((v) => (
            <FeaturedVideoCard key={v.id} v={v} />
          ))}
        </div>
      </div>
    </section>
  )
}
